import { useCallback, useState, useEffect } from "react";
import { Device } from "data/device";
import useTrigger from "hooks/useTrigger";

/**
 * Custom hook to track scroll progress, scroll direction, and viewport width.
 */
function useMediaQuery() {
  const [isMobile, setMobile] = useState(false); // Tracks if the viewport is mobile
  const [isTablet, setTablet] = useState(false); // Tracks if the viewport is tablet
  const [isLaptop, setLaptop] = useState(false); // Tracks if the viewport is laptop
  const [progress, setProgress] = useState(0); // Tracks scroll progress as a percentage
  const [scroll, setScroll] = useState(false); // Tracks scroll direction (up or down)
  const [orientation, setOrientation] = useState("landscape"); // Tracks the current orientation
  const [top, setTop] = useState(0); // Tracks the current scroll position
  const [width, setWidth] = useState(0); // Tracks the viewport width

  /**
   * Handles scroll events and updates the scroll direction and position.
   */
  const scrollUp = useCallback(() => {
    const pY = window.scrollY;

    // Update scroll direction
    if (pY < top && !scroll) setScroll(true);
    else if (pY > top && scroll) setScroll(false);

    setTop(pY); // Update the current scroll position
  }, [top, scroll]);

  /**
   * Sets up the scroll event listener and cleans it up on unmount.
   */
  useTrigger("scroll", scrollUp);

  /**
   * Calculates and updates the scroll progress as a percentage.
   */
  useEffect(() => {
    const height = document.body.clientHeight;
    const screen = window.innerHeight;

    // Avoid division by zero and calculate progress
    if (height > screen) {
      setProgress(Math.round((top / (height - screen)) * 10000 || 0) / 100);
    }
  }, [top]);

  /**
   * Updates the viewport width on mount.
   */
  useEffect(() => {
    setWidth(document.body.clientWidth);
  }, []);

  /**
   * Sets up the resize event listener and cleans it up on unmount.
   * Updates the viewport width on resize.
   */
  useTrigger("resize", () => {
    setWidth(document.body.clientWidth);
  });

  /**
   * Handles window resize events and updates the viewport width.
   */
  useEffect(() => {
    setLaptop(width <= Device.LG);
    setTablet(width <= Device.MD);
    setMobile(width <= Device.SM);
  }, [width]);

  /**
   * Handles orientation change events and updates the orientation state.
   * This is useful for responsive designs that depend on orientation.
   */
  useEffect(() => {
    const isPortrait = window.matchMedia("(orientation: portrait)").matches;
    setOrientation(isPortrait ? "portrait" : "landscape");
  }, [width]);

  return {
    isTablet, // Boolean indicating if the viewport is tablet
    isMobile, // Boolean indicating if the viewport is mobile
    isLaptop, // Boolean indicating if the viewport is laptop
    isLandscape: orientation === "landscape" && width <= 932,
    orientation, // Current orientation of the device
    progress, // Scroll progress as a percentage
    scroll, // Scroll direction (true for up, false for down)
    top, // Current scroll position
    width, // Viewport width
  };
}

export default useMediaQuery;
